import React, { useMemo, useRef, useState } from 'react';
import {
  Landmark, BookOpen, GraduationCap, Thermometer, Sparkles, Bug, FlaskConical, Route, Wheat, Droplets, ShieldAlert,
  BadgeCheck, HardHat, Stethoscope, Flame, Folder, ChevronLeft, ChevronRight, Search, FileText, ScrollText, Loader2, X,
} from 'lucide-react';
import { dataIt } from '../haccpApi';

export const ICONE = {
  landmark: Landmark, 'book-open': BookOpen, 'graduation-cap': GraduationCap, thermometer: Thermometer, sparkles: Sparkles, bug: Bug,
  'flask-conical': FlaskConical, route: Route, wheat: Wheat, droplets: Droplets, 'shield-alert': ShieldAlert, 'badge-check': BadgeCheck,
  'hard-hat': HardHat, stethoscope: Stethoscope, flame: Flame, folder: Folder,
};

const conteggio = (voci) => {
  const doc = voci.filter((v) => v.tipo === 'documento').length;
  const reg = voci.length - doc;
  return [doc && `${doc} document${doc === 1 ? 'o' : 'i'}`, reg && `${reg} registr${reg === 1 ? 'o' : 'i'}`].filter(Boolean).join(' · ');
};

const CartellaCard = ({ c, onClick, indice }) => {
  const Icona = ICONE[c.icona] || Folder;
  return (
    <button type="button" onClick={onClick} data-testid={`inspector-folder-card-${c.codice}`} style={{ animationDelay: `${indice * 45}ms` }}
      className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both group text-left bg-white rounded-2xl border border-slate-200 p-4 flex gap-3 items-start hover:border-slate-900 hover:-translate-y-0.5 hover:shadow-lg transition-[transform,box-shadow,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900">
      <span className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0"><Icona className="h-5 w-5" /></span>
      <span className="flex-1 min-w-0">
        <span className="block font-bold text-slate-900 leading-tight">{c.nome}</span>
        <span className="block text-xs text-slate-500 mt-0.5 line-clamp-2">{c.descrizione}</span>
        <span className="mt-2.5 flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{conteggio(c.voci)}</span>
        </span>
      </span>
      <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-slate-900 group-hover:translate-x-0.5 transition-transform mt-1" />
    </button>
  );
};

// Scelta utente: all'ispettore solo la data del documento, niente scadenze né stato
const sottotitolo = (v, cartella) => {
  if (v.tipo === 'registro') return 'PDF creato al momento con i dati registrati';
  const tipo = cartella && v.persona && v.titolo;
  return [tipo, v.data_documento ? `del ${dataIt(v.data_documento)}` : (v.precedente ? 'versione precedente' : '')].filter(Boolean).join(' · ');
};

const VoceRiga = ({ v, onApri, aprendo, cartella }) => {
  const reg = v.tipo === 'registro';
  const Icona = reg ? ScrollText : FileText;
  return (
    <button type="button" onClick={() => onApri(v)} data-testid={reg ? `inspector-register-row-${v.id.replace(':', '-')}` : `inspector-doc-row-${v.id}`}
      className="w-full text-left flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-slate-900 active:scale-[0.99] transition-[transform,border-color]">
      <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${reg ? 'bg-sky-50 text-sky-700' : 'bg-slate-100 text-slate-700'}`}><Icona className="h-4 w-4" /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-slate-900 truncate">{v.persona || v.titolo}</span>
        <span className="block text-xs text-slate-500 line-clamp-2">{cartella ? `${cartella} · ` : ''}{sottotitolo(v, cartella)}</span>
      </span>
      {v.precedente && <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-600 whitespace-nowrap">Precedente</span>}
      {aprendo === v.id ? <Loader2 className="h-4 w-4 animate-spin text-slate-900 shrink-0" />
        : <><span className="hidden sm:inline text-xs font-bold text-slate-900">Apri</span><ChevronRight className="sm:hidden h-4 w-4 text-slate-400 shrink-0" /></>}
    </button>
  );
};

const Gruppo = ({ titolo, voci, ...rest }) => (voci.length > 0 && (
  <section className="space-y-2">
    <h3 className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{titolo} · {voci.length}</h3>
    {voci.map((v) => <VoceRiga key={v.id} v={v} {...rest} />)}
  </section>
));

const DettaglioCartella = ({ c, onBack, ...rest }) => {
  const Icona = ICONE[c.icona] || Folder;
  return (
    <div className="space-y-5 animate-in fade-in slide-in-from-right-2 duration-300" data-testid={`inspector-folder-detail-${c.codice}`}>
      <button type="button" onClick={onBack} data-testid="inspector-back-to-folders"
        className="text-sm font-semibold text-slate-700 hover:text-slate-950 flex items-center gap-1"><ChevronLeft className="h-4 w-4" />Tutte le cartelle</button>
      <div className="flex items-start gap-3">
        <span className="w-12 h-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center shrink-0"><Icona className="h-6 w-6" /></span>
        <div><h2 className="text-xl font-extrabold text-slate-950 tracking-tight">{c.nome}</h2><p className="text-sm text-slate-600">{c.descrizione}</p></div>
      </div>
      <Gruppo titolo="Documenti" voci={c.voci.filter((v) => v.tipo === 'documento' && !v.precedente)} {...rest} />
      <Gruppo titolo="Registri" voci={c.voci.filter((v) => v.tipo === 'registro')} {...rest} />
      <Gruppo titolo="Versioni precedenti" voci={c.voci.filter((v) => v.precedente)} {...rest} />
    </div>
  );
};

const Ricerca = ({ q, setQ }) => (
  <div className="relative">
    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10 pointer-events-none" />
    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca documento o persona" data-testid="inspector-search-input"
      aria-label="Cerca" className="w-full h-12 pl-11 pr-10 rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-md shadow-sm text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900" />
    {q && <button type="button" onClick={() => setQ('')} aria-label="Cancella ricerca" data-testid="inspector-search-clear"
      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:text-slate-900"><X className="h-4 w-4" /></button>}
  </div>
);

export const CartellaVista = ({ dati, onApri, aprendo }) => {
  const [aperta, setAperta] = useState(null);
  const [q, setQ] = useState('');
  const inizio = useRef(null);
  const vai = (codice) => { setAperta(codice); inizio.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); };
  const cartelle = useMemo(() => dati.cartelle.filter((c) => c.voci.length), [dati]);
  const risultati = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return null;
    return cartelle.flatMap((c) => c.voci.filter((v) => `${v.titolo} ${v.persona || ''} ${c.nome}`.toLowerCase().includes(t)).map((v) => ({ v, c })));
  }, [q, cartelle]);
  const scelta = cartelle.find((c) => c.codice === aperta);
  return (
    <div className="space-y-5 scroll-mt-4" data-testid="inspector-folders" ref={inizio}>
      <Ricerca q={q} setQ={setQ} />
      {risultati && (
        <div className="space-y-2" data-testid="inspector-search-results">
          {risultati.map(({ v, c }) => <VoceRiga key={`${c.codice}-${v.id}`} v={v} cartella={c.nome} onApri={onApri} aprendo={aprendo} />)}
          {!risultati.length && <p className="text-sm text-slate-500 py-6 text-center">Nessun risultato per “{q}”</p>}
        </div>
      )}
      {!risultati && scelta && <DettaglioCartella c={scelta} onBack={() => vai(null)} onApri={onApri} aprendo={aprendo} />}
      {!risultati && !scelta && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {cartelle.map((c, i) => <CartellaCard key={c.codice} c={c} indice={i} onClick={() => vai(c.codice)} />)}
          {!cartelle.length && <p className="text-sm text-slate-500 py-8" data-testid="inspector-empty">Nessun documento disponibile in questo momento.</p>}
        </div>
      )}
    </div>
  );
};
